import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { FileText, X } from 'lucide-react';

const TERMS_CONTENT = `DIGITAL FINANCIAL SERVICES AGREEMENT

TERMS AND CONDITIONS

YOUSE CAPITAL

Last Updated: March 2026

This Digital Financial Services Agreement ("Agreement") governs the access to and use of the financial technology platform operated by Youse Capital ("Youse Capital", "Platform", "Company", "We", "Us", or "Our").

By accessing the website, opening an account, or using any services provided through the Platform, you ("User" or "Client") acknowledge that you have read, understood, and agree to be legally bound by this Agreement.

If you do not agree with these Terms, you must immediately discontinue use of the Platform.

⸻

1. COMPANY STRUCTURE

Youse Capital operates a digital financial technology platform that provides access to financial infrastructure and technology services designed to facilitate international financial transactions and digital asset operations.

The Platform integrates with financial infrastructure providers, liquidity providers, regulated financial institutions, and financial technology partners.

Sparta Capital operates as an institutional financial infrastructure provider that may maintain operational settlement accounts, custody structures, or master accounts used for transaction settlement and liquidity management.

Connections between Youse Capital, Sparta Capital, and third-party financial institutions may occur through secure Application Programming Interface (API) integrations.

Youse Capital does not operate as a traditional deposit-taking bank.

⸻

2. DEFINITIONS

For the purposes of this Agreement:

Platform
The digital infrastructure, systems, and financial technology services operated by Youse Capital.

Client
Any individual or legal entity that has successfully completed onboarding and identity verification procedures.

Digital Assets
Cryptocurrencies supported by the platform including but not limited to Bitcoin (BTC), Ethereum (ETH), and Tether (USDT).

Fiat Currency
Government-issued currencies including but not limited to USD, EUR, GBP, and other supported currencies.

Master Accounts
Institutional operational accounts maintained by infrastructure providers or financial institutions used to facilitate financial settlement, liquidity, and custody operations.

Subaccounts
Digital ledger accounts assigned to individual clients within the platform infrastructure for the purpose of recording balances and transactions.

⸻

3. NATURE OF SERVICES

Youse Capital provides a financial technology platform that may include the following services:

• digital multi-currency account infrastructure
• international payment processing
• digital asset swap and conversion services
• fiat-to-crypto and crypto-to-fiat conversion
• financial settlement infrastructure
• liquidity access through financial partners
• digital financial management tools

The Platform provides technological access to financial infrastructure but does not directly provide traditional banking services.

Certain financial services may be executed through third-party institutions connected to the Platform.

⸻

4. ACCOUNT STRUCTURE AND CUSTODY

Client funds processed through the Platform may be maintained through financial infrastructure structures including:

• institutional custody accounts
• omnibus settlement accounts
• operational liquidity accounts
• client subaccounts maintained within the Platform ledger

Operational settlement accounts used for transaction processing may be maintained through institutional partners including infrastructure providers such as Sparta Capital.

Client balances displayed within the Platform represent digital ledger entries corresponding to financial transactions processed through the financial infrastructure connected to the Platform.

⸻

5. CLIENT SUBACCOUNTS

Upon successful onboarding, clients may be provided access to digital subaccounts within the Platform.

Subaccounts may display balances including:

• fiat currency balances
• digital asset balances
• transaction records
• financial activity logs

Subaccounts represent internal ledger records of financial operations processed through the Platform infrastructure.

⸻

6. DIGITAL ASSET SERVICES

The Platform may support operations involving digital assets including:

• cryptocurrency transfers
• cryptocurrency swaps
• digital asset custody infrastructure
• fiat-to-crypto conversion
• crypto-to-fiat conversion

Supported digital assets may include but are not limited to:

• Bitcoin (BTC)
• Ethereum (ETH)
• Tether (USDT)

Digital asset transactions rely on blockchain networks which operate independently of the Platform.

⸻

7. CRYPTO SWAP SERVICES

Clients may conduct digital asset swaps including:

• BTC to USDT
• ETH to BTC
• USDT to ETH
• other supported pairs

Swap pricing may be determined by liquidity providers connected to the Platform.

Execution times depend on network conditions and liquidity availability.

⸻

8. FIAT TO CRYPTO CONVERSION

Clients may convert supported fiat currencies into supported digital assets.

Conversion may occur through:

• liquidity providers
• OTC trading desks
• digital asset exchanges
• institutional market makers

Rates may vary depending on market conditions.

⸻

9. CRYPTO TO FIAT CONVERSION

Clients may convert supported digital assets into fiat currencies.

Fiat settlement may occur through financial institutions connected to the Platform infrastructure.

Processing times may vary depending on financial network settlement times.

⸻

10. INTERNATIONAL TRANSACTIONS

The Platform enables international financial transactions including cross-border payments.

Transaction processing may involve:

• banking networks
• payment rails
• financial institutions
• intermediary settlement partners

Settlement times may vary based on jurisdiction and financial infrastructure.

⸻

11. CLIENT ELIGIBILITY

To use the Platform, users must:

• be at least 18 years of age
• possess legal capacity to enter into contracts
• provide accurate information during onboarding
• comply with all applicable laws

The Platform reserves the right to refuse service at its discretion.

⸻

12. KYC AND IDENTITY VERIFICATION

All clients must complete identity verification procedures including:

• government-issued identification
• corporate documentation (for legal entities)
• proof of address
• beneficial ownership verification
• source of funds verification

Additional documentation may be required at any time.

⸻

13. AML AND COMPLIANCE

The Platform maintains Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF) policies.

Transactions may be monitored for suspicious activity.

The Platform reserves the right to:

• freeze accounts
• suspend transactions
• report suspicious activity to authorities

⸻

14. FEES

Certain services may involve fees including:

• transaction processing fees
• conversion fees
• liquidity provider spreads
• blockchain network fees
• administrative fees

Fees may vary depending on the type of service used.

⸻

15. RISK DISCLOSURE

Clients acknowledge that financial transactions involve risks including:

• cryptocurrency volatility
• foreign exchange fluctuations
• network congestion
• counterparty risks
• liquidity risks

The Platform does not guarantee financial returns.

⸻

16. LIMITATION OF LIABILITY

To the maximum extent permitted by law, Youse Capital shall not be liable for:

• financial losses resulting from market fluctuations
• blockchain network failures
• third-party service interruptions
• delays in financial infrastructure networks

Services are provided on an "as available" basis.

⸻

17. ACCOUNT SUSPENSION

Accounts may be suspended or terminated if:

• the client violates this Agreement
• suspicious activity is detected
• regulatory risks arise
• false information is provided

⸻

18. INTELLECTUAL PROPERTY

All Platform software, trademarks, design elements, and systems remain the intellectual property of Youse Capital or its licensors.

Unauthorized use is strictly prohibited.

⸻

19. MODIFICATIONS TO TERMS

Youse Capital reserves the right to modify this Agreement at any time.

Updated versions will be published on the Platform.

Continued use constitutes acceptance of the updated Terms.

⸻

20. GOVERNING LAW

This Agreement shall be governed by the laws applicable to the jurisdiction in which the operating entity of Youse Capital is registered.

Any disputes shall be resolved in the competent courts of that jurisdiction.

⸻

21. CONTACT INFORMATION

For questions regarding these Terms, users may contact the Platform through the official contact channels available on the Youse Capital website.`;

export const TermsAndConditionsModal = ({ isOpen, onClose, onAccept, accountType = 'PERSONAL' }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      setAccepted(false);
    }
  }, [isOpen]);

  const endRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !endRef.current || !scrollRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setHasScrolledToBottom(true),
      { root: scrollRef.current, threshold: 0 }
    );
    observer.observe(endRef.current);
    return () => observer.disconnect();
  }, [isOpen]);

  const handleScroll = (e) => {
    const element = e.target;
    if (Math.ceil(element.scrollTop + element.clientHeight) >= element.scrollHeight - 80) {
      setHasScrolledToBottom(true);
    }
  };

  const jumpToEnd = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setHasScrolledToBottom(true);
  };

  const handleAccept = () => {
    if (accepted && hasScrolledToBottom) {
      onAccept();
    }
  };

  const getAccountTypeName = () => {
    switch (accountType) {
      case 'BUSINESS':
        return 'Business Account';
      case 'COMMERCIAL_OFFICER':
        return 'Commercial Officer';
      case 'SINGLE':
        return 'Single Account (Free)';
      default:
        return 'Personal Account';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] mx-4 flex flex-col" data-testid="terms-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Terms and Conditions</h2>
              <p className="text-white/70 text-sm">{getAccountTypeName()} - Youse Capital</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
            data-testid="terms-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scroll instruction */}
        {!hasScrolledToBottom && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 text-amber-800 text-sm flex items-center gap-2">
            <span className="animate-bounce">↓</span>
            <span className="flex-1">Please scroll to the bottom to read the entire agreement before accepting.</span>
            <button type="button" onClick={jumpToEnd} className="underline font-semibold whitespace-nowrap" data-testid="terms-jump-to-end-btn">
              Go to end
            </button>
          </div>
        )}

        {/* Content */}
        <div 
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 bg-gray-50"
          style={{ maxHeight: 'calc(90vh - 280px)' }}
        >
          <div className="prose prose-sm max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
              {TERMS_CONTENT}
            </pre>
            <div ref={endRef} className="h-1" data-testid="terms-end-sentinel" />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-white rounded-b-xl">
          <div className="flex items-start gap-3 mb-4">
            <Checkbox
              id="accept-terms"
              checked={accepted}
              onCheckedChange={setAccepted}
              disabled={!hasScrolledToBottom}
              className="mt-1"
              data-testid="terms-checkbox"
            />
            <label 
              htmlFor="accept-terms" 
              className={`text-sm cursor-pointer ${!hasScrolledToBottom ? 'text-gray-400' : 'text-gray-700'}`}
            >
              I have read and understood the Digital Financial Services Agreement. I agree to be legally bound by these Terms and Conditions.
            </label>
          </div>
          
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} data-testid="terms-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!accepted || !hasScrolledToBottom}
              className="bg-[#1a365d] hover:bg-[#2d4a7c]"
              data-testid="terms-accept-btn"
            >
              Accept & Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsModal;
